import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Percent, Plus, Search, Edit2, Trash2, Ship, FileText } from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface CustomsDuty {
  _id: string;
  name: string;
  rate: number;
  description?: string;
  isActive: boolean;
}

const CustomsDutiesPage: React.FC = () => {
  const [customsDuties, setCustomsDuties] = useState<CustomsDuty[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomsDuty, setEditingCustomsDuty] = useState<CustomsDuty | null>(null);
  const [formData, setFormData] = useState({ name: '', rate: 0, description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCustomsDuties();
  }, []);

  const fetchCustomsDuties = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCustomsDuties();
      setCustomsDuties(response.data.customsDuties || []);
    } catch (error: any) {
      console.error('Error fetching customs duties:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch customs duty rates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (customsDuty?: CustomsDuty) => {
    if (customsDuty) {
      setEditingCustomsDuty(customsDuty);
      setFormData({ name: customsDuty.name, rate: customsDuty.rate, description: customsDuty.description || '' });
    } else {
      setEditingCustomsDuty(null);
      setFormData({ name: '', rate: 0, description: '' });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCustomsDuty(null);
    setFormData({ name: '', rate: 0, description: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'rate' ? parseFloat(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || formData.rate < 0) {
      toast.error('Customs duty name and a non-negative rate are required');
      return;
    }
    try {
      setSubmitting(true);
      if (editingCustomsDuty) {
        await apiService.updateCustomsDuty(editingCustomsDuty._id, formData);
        toast.success('Customs duty updated successfully');
      } else {
        await apiService.createCustomsDuty(formData);
        toast.success('Customs duty created successfully');
      }
      handleCloseModal();
      fetchCustomsDuties();
    } catch (error: any) {
      console.error('Error saving customs duty:', error);
      toast.error(error.response?.data?.error || 'Failed to save customs duty');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this customs duty rate?')) return;
    try {
      await apiService.deleteCustomsDuty(id);
      toast.success('Customs duty deleted successfully');
      fetchCustomsDuties();
    } catch (error: any) {
      console.error('Error deleting customs duty:', error);
      toast.error(error.response?.data?.error || 'Failed to delete customs duty');
    }
  };

  const filteredCustomsDuties = customsDuties.filter(customsDuty =>
    customsDuty.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Name', accessor: (row: CustomsDuty) => row.name },
    { header: 'Rate (%)', accessor: (row: CustomsDuty) => `${row.rate}%` },
    { header: 'Description', accessor: (row: CustomsDuty) => row.description || '-' },
    {
      header: 'Actions',
      accessor: (row: CustomsDuty) => (
        <div className="flex gap-0.5">
          <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" icon={<Edit2 className="w-4 h-4" />} onClick={() => handleOpenModal(row)} />
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" icon={<Trash2 className="w-4 h-4" />} onClick={() => handleDelete(row._id)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>Customs Duty, Freight & Etc</h1>
          <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'var(--font-family)' }}>Manage customs duty, freight, and other charges</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Rate</Button>
      </motion.div>

      <Card padding="md">
        <Input
          placeholder="Search rates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<Search className="w-5 h-5" />}
          fullWidth
        />
      </Card>

      <Card padding="none">
        {loading ? (
          <div className="flex justify-center items-center py-12"><LoadingSpinner size="lg" /></div>
        ) : filteredCustomsDuties.length > 0 ? (
          <Table data={filteredCustomsDuties} columns={columns} emptyMessage="No rates found" />
        ) : (
          <div className="px-6 py-12 text-center">
            <Ship className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>No rates yet</h3>
            <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>Get started by adding your first rate.</p>
            <div className="mt-6">
              <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Rate</Button>
            </div>
          </div>
        )}
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingCustomsDuty ? 'Edit Rate' : 'Add New Rate'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Standard Customs Duty, Freight Charges"
            required
            fullWidth
            icon={<Ship className="w-5 h-5 text-gray-400" />}
          />
          <Input
            label="Rate (%)"
            name="rate"
            type="number"
            value={formData.rate}
            onChange={handleChange}
            placeholder="e.g., 10"
            required
            fullWidth
            icon={<Percent className="w-5 h-5 text-gray-400" />}
          />
          <Input
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter description (optional)"
            fullWidth
            icon={<FileText className="w-5 h-5 text-gray-400" />}
          />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editingCustomsDuty ? 'Update Rate' : 'Create Rate'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomsDutiesPage;

